import { VoiceContentStateTypes } from 'pages/ContentVoiceBuilderPage/types';
import { FolderBreadcrumbData } from 'domains/core/components/folders/types';
import { Folder } from 'models/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

export type BeeConfig = {
    access_token: string;
    client_uid: string;
    mergeTags: MergeTag[];
    contentDefaults: any;
    editorFonts: any;
    autosave: number | boolean;
    defaultForm?: any;
};

export type MergeTag = {
    name: string;
    value: string;
    previewValue: string;
};

export type SenderProfile = {
    id: number;
    name: string;
    from_address: string;
    reply_address: string;
};

export type DirectMailSenderProfile = {
    id: number;
    name: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    stateCode?: string;
    email?: string;
    phoneNumber?: string;
    jobTitle?: string;
    zipCode?: string;
    countryCode?: string;
};

export type VoiceCallerProfile = {
    id: number;
    name: string;
    number?: string;
    countryCode?: string;
    isVerified?: boolean;
};

export type SMSSenderProfile = {
    id: number;
    name: string;
    number?: string;
    countryCode?: string;
    isVerified?: boolean;
};

export enum ContentType {
    ALL = 'all',
    FOLDER = 'folder',
    EMAIL = 'email',
    PAGE = 'page',
    DIRECT_MAIL = 'direct_mail',
    SMS = 'sms',
    VOICE = 'voice',
    WHATSAPP = 'whatsapp',
}

export type ContentTag = {
    applicationsAccess: ApplicationAccessType | string;
    communicationType: CommunicationType;
};

export type DirectMail = ContentTag & {
    id: number;
    name: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    html: string | null;
    design: any; // TODO: use real type from unlayer
    type?: string;
    isArchived?: boolean;
};

export type VoiceContent = {
    id: number;
    name: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    type?: string;
    isArchived?: boolean;
};

export type Template = AssessmentData &
    ContentTag & {
        id: number;
        name: string;
        html: string;
        createdTimestamp: string;
        lastModifiedTimestamp: string;
        isLayout: boolean;
        beeJson?: string;
        preHeader?: string;
        senderProfileId?: number;
        subjectLine?: string;
        // This is only used by the type guard
        templateFields?: any;
        // This is neccessary as we are passing Template | LandingPage | Content as the type to the
        // ContentTable component. We have a new key 'type' on the content API and hence this needs to
        // be added as optional to both Template and LandingPage types.
        type?: string;
        isArchived?: boolean;
        status?: ContentStatus;
    };

export type LandingPage = {
    beeJson?: string;
    createdTimestamp: string;
    deletedTimestamp?: string;
    id: number;
    lastModifiedTimestamp: string;
    name: string;
    parentFolderId?: number;
    type?: string;
    isArchived?: boolean;
};

export type SMS = AssessmentData &
    ContentTag & {
        content: string;
        createdTimestamp: string;
        defaultTemplateData: object;
        id: number;
        lastModifiedTimestamp: string;
        name: string;
        parentFolderId?: number;
        templateFields: string;
        type?: string;
        isArchived?: boolean;

        autoResponses?: SMSAutoResponse[];
        canDelete?: boolean;
        status?: ContentStatus;
    };

export type WhatsAppComponent = {
    type: 'BODY' | 'BUTTONS';
    format?: 'TEXT' | 'IMAGE';
    text?: string;
};

export type WhatsApp = ContentTag & {
    id: number;
    name: string;
    language: string;
    components: WhatsAppComponent[];
    defaultTemplateData: object;
    templateFields: Record<string, string>;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    deletedAt?: string | null;
    parentFolderId?: number;
    type?: string;
    isArchived?: boolean;
    canDelete?: boolean;
    status?: ContentStatus;
    whatsappAccountId?: string;
    whatsappNamespace?: string;
    whatsappTemplateId?: string;
    // For compatibility with editor - extracted from BODY component
    content?: string;
};

export type VoiceCallContent = ContentTag & {
    id: number;
    name: string;
    voiceJson: VoiceContentStateTypes['voiceJson'];
    defaultTemplateData: string;
    templateFields: string;
    createdTimestamp: string;
    lastModifiedTimestamp: string;
    type?: string;
    isArchived?: boolean;
    status?: string;
};

export type AssessmentData = {
    assessmentId?: string;
    assessmentName?: string;
    assessmentConsentRequired?: boolean;
};

export type SMSAutoResponse = {
    keywords: string[];
    text: string;
    validFor?: {
        interval: number;
        units: 'days' | 'hours';
    };
};

export type CreateLandingPageInput = Pick<LandingPage, 'beeJson'> &
    Partial<Pick<LandingPage, 'name' | 'parentFolderId'>>;
export type CreateTemplateInput = Pick<Template, 'beeJson'> &
    ContentTag & {
        parentFolderId?: number;
        templateName?: string;
    };
export type CreateVoiceContentInput = {
    name?: string;
    voiceJson?: VoiceContentStateTypes['voiceJson'];
};
export type UpdateLandingPageInput = Pick<LandingPage, 'beeJson'> &
    Partial<Pick<LandingPage, 'name' | 'parentFolderId'>> & { id: string };
export type UpdateTemplateInput = Pick<Template, 'beeJson'> &
    ContentTag &
    AssessmentData & {
        id: string;
        parentFolderId?: number;
        preHeader?: string;
        senderProfileId?: number;
        subjectLine?: string;
        templateName?: string;
    };
export type UpdateVoiceContentInput = {
    id: string;
    name?: string;
    type?: ContentType;
    parentFolderId?: number;
    voiceJson?: VoiceContentStateTypes['voiceJson'];
};

export type UpdateContentStatusInput = {
    status: string;
    channel: string;
};

export function isTemplate(obj: any): obj is Template {
    return obj && typeof obj === 'object' && 'beeJson' in obj && 'templateFields' in obj;
}
export function isLandingPage(obj: any): obj is LandingPage {
    return obj && typeof obj === 'object' && 'beeJson' in obj && !('templateFields' in obj);
}

export enum ContentTypeEnumForBackend {
    'Default' = '',
    'Direct Mail' = 'direct_mail',
    'Email' = 'email',
    'Folder' = 'folder',
    'Landing Page' = 'landing_page',
    'SMS' = 'sms',
    'voice' = 'voice',
    'Whatsapp' = 'whatsapp',
}

export enum ContentTypeForComparison {
    DEFAULT = 'Default',
    DIRECT_MAIL = 'Direct Mail',
    EMAIL = 'Email',
    FOLDER = 'Folder',
    LANDING_PAGE = 'Landing Page',
    SMS = 'SMS',
    VOICE = 'voice',
    WHATSAPP = 'Whatsapp',
}

export type ContentModalContentType =
    | DirectMail
    | Template
    | LandingPage
    | SMS
    | WhatsApp
    | Folder
    | FolderBreadcrumbData
    | VoiceContent;

export const RootContentFolder = {
    name: 'All Content',
    id: -1,
    type: ContentTypeForComparison.FOLDER,
};

export enum CommunicationType {
    COMMERCIAL = 'commercial',
    TRANSACTIONAL = 'transactional',
}

export enum ApplicationAccessType {
    OUTREACH = 'outreach',
    INCARE = 'in_care',
    INJOURNEY = 'in_journey',
    ACCESS = 'access',
}
